import React from "react";
import {Badge, Button, Chip, IconButton, Typography} from "@mui/material";
import EntityTable, {EntityTableHeaderProps} from "../../components/list/EntityList";
import {useAuth} from "../../utils/hooks/useAuth";
import useTableParams from "../../components/list/useTableParams";
import {useNavigate} from "react-router-dom";
import {useConfirm} from "material-ui-confirm";
import {useNotification} from "../../utils/hooks/useNotification";
import buildUrl from "../../utils/api/buildUrl";
import useSWR from "swr";
import {deleteFetcher, getFetcher, putFetcher} from "../../utils/api/fetchers";
import {Boat, PaginatedBoats} from "../../schemas/boats/boatsSchema";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import InfoIcon from "@mui/icons-material/Info";
import {Category} from "../../schemas/category/categoriesSchema";
import UserRole from "../../schemas/user/roles";
import WarningIcon from "@mui/icons-material/Warning";
import {getCategoryColor} from "../../utils/categories/categoryColors";

const Boats: React.FC = () => {
    const {hasPermission} = useAuth();
    const {sortKeys, sortDirections, page, rowsPerPage, filters, setFilter, setPage} = useTableParams();
    const navigate = useNavigate();
    const confirm = useConfirm();
    const {showNotification} = useNotification();

    const url = buildUrl('/boats', {
        page,
        size: rowsPerPage,
        sortBy: sortKeys,
        sortDirections,
        filters
    });
    const {data, mutate} = useSWR<PaginatedBoats>(url, getFetcher, {keepPreviousData: true});
    const {data: unofficialBoats, mutate: mutateUnofficial} = useSWR<Boat[]>('/boats/unofficial', getFetcher);
    const {data: categories} = useSWR<Category[]>('/categories', getFetcher);
    const editPermission = hasPermission(UserRole.ORGANIZER);
    const deleteMarkOfficialPermission = hasPermission(UserRole.COMMODORE);

    const handleCreateBoat = () => {
        navigate('/boats/create');
    }

    const handleDetails = (boatId: number) => {
        navigate(`/boats/${boatId}`);
    }

    const handleDelete = (boatId: number) => {
        confirm({
            description: `Opravdu chcete smazat tuto loď?`,
            title: "Smazat loď",
            confirmationText: "Smazat",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "error"},
        })
            .then(() => {
                deleteFetcher(`/boats/${boatId}`).then(() => {
                    showNotification('Loď byla smazána', 'success');
                    if (data?.content.length === 1 && data.page.number > 0) {
                        setPage(data.page.number - 1);
                    } else {
                        mutate();
                        mutateUnofficial();
                    }
                }).catch(() => {
                    showNotification('Loď se nepodařilo smazat', 'error');
                });
            }).catch(() => {
        });
    }

    const handleMarkOfficial = (boatId: number) => {
        confirm({
            description: <>
                <Typography>Opravdu chcete schválit tuto loď?</Typography>
                <Typography>Pro schválení lodi je zapotřebí role komodora.</Typography>
            </>,
            title: "Schválit loď",
            confirmationText: "Schválit",
            cancellationText: "Zrušit",
            confirmationButtonProps: {variant: "contained", color: "success"},
        })
            .then(() => {
                putFetcher(`/boats/${boatId}/register`, {}).then(() => {
                    showNotification('Loď byla schválena', 'success');
                    mutate();
                    mutateUnofficial();
                }).catch((err) => {
                    if (err.response?.status === 403) {
                        showNotification('Nemáte oprávnění schválit loď', 'error');
                        return;
                    }
                    showNotification('Loď se nepodařilo schválit', 'error');
                });
            }).catch(() => {
        });
    }

    return (
        <EntityTable
            title="Lodě"
            controls={editPermission ? [
                unofficialBoats && unofficialBoats.length > 0 && (
                    <IconButton key="unofficial" color="warning"
                                onClick={() => setFilter('officialRegistered', 'False')}>
                        <Badge badgeContent={unofficialBoats.length} color="error" title="Zobrazit neschválené lodě">
                            <WarningIcon/>
                        </Badge>
                    </IconButton>
                ),
                <Button key="add" variant="contained" color="success" onClick={handleCreateBoat}>
                    Vytvořit loď
                </Button>
            ] : []}
            headers={[
                {key: 'name', label: 'Jméno', sortable: true, filtered: true},
                {
                    key: 'category',
                    label: 'Kategorie',
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: 'multiSelect',
                        options: [
                            ...(categories || []).map(category => ({
                                label: category.name,
                                value: category.categoryNumber.toString()
                            }))
                        ]
                    },
                    render: (category) => (
                        <span style={{color: getCategoryColor(category.categoryNumber), fontWeight: 'bold'}}>
                            {category.name}
                        </span>
                    )
                },
                {
                    key: 'type',
                    label: 'Typ',
                    sortable: true,
                    filtered: true,
                    render: (type: string) => type ||
                        <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>
                },
                {
                    key: 'length',
                    label: 'Délka',
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: 'number',
                        min: 0
                    },
                    render: (length: number) => length ? <Typography>{length.toFixed(2)} m</Typography> :
                        <Typography color="textSecondary" fontStyle="italic">Neuvedeno</Typography>,
                },
                ...(editPermission ? [{
                    key: 'officialRegistered',
                    label: 'Schváleno',
                    sortable: true,
                    filtered: true,
                    filterConfig: {
                        type: 'select',
                        options: [
                            {label: 'Vše', value: ''},
                            {label: 'Ano', value: 'True'},
                            {label: 'Ne', value: 'False'}
                        ]
                    },
                    render: (officialRegistered: boolean) => <Chip
                        label={officialRegistered ? 'Ano' : 'Ne'}
                        color={officialRegistered ? 'success' : 'error'}
                        sx={{width: 'fit-content', minWidth: "50px"}}
                    />
                }] : []),
                {
                    key: 'controls',
                    render:
                        (boat: Boat) => (
                            <div>
                                {editPermission ? (
                                    <>
                                        {(!boat.officialRegistered && deleteMarkOfficialPermission) && (
                                            <IconButton color="warning" title="Neschválená loď"
                                                        onClick={() => handleMarkOfficial(boat.id)}>
                                                <WarningIcon/>
                                            </IconButton>
                                        )}
                                        <IconButton color="info" title="Detaily a úprava"
                                                    onClick={() => handleDetails(boat.id)}>
                                            <EditIcon/>
                                        </IconButton>
                                        {deleteMarkOfficialPermission && (
                                            <IconButton color="error" title="Smazat"
                                                        onClick={() => handleDelete(boat.id)}>
                                                <DeleteIcon/>
                                            </IconButton>
                                        )}
                                    </>
                                ) : (
                                    <IconButton color="info" title="Detaily" onClick={() => handleDetails(boat.id)}>
                                        <InfoIcon/>
                                    </IconButton>
                                )}
                            </div>
                        )
                }
            ] as EntityTableHeaderProps[]}
            entities={data?.content || []}
            entitiesCount={data?.page.totalElements ?? 0}
            indexes
        />
    );
}

export default Boats;